import { createTheme, alpha } from '@mui/material/styles';
import { ptBR } from '@mui/material/locale';

// Identidade Contblack: preto + verde da marca.
// No painel administrativo o verde é usado só como acento (estado ativo, indicadores),
// mantendo a interface sóbria e com foco na leitura dos dados.
export const brand = {
    green: '#1EFF84',
    greenDark: '#0E9F5A',
    ink: '#0F1115',
    inkSoft: '#1A1D23',
    inkBorder: '#262A31',
};

// Paleta validada (CVD/contraste) para até 2 séries: verde da marca + laranja.
export const chartColors = {
    primary: '#1BAF7A',
    secondary: '#EB6834',
    neutral: '#98A2B3',
    grid: '#EAECF0',
    axis: '#667085',
};

const theme = createTheme(
    {
        palette: {
            mode: 'light',
            primary: { main: '#16181D', light: '#2B2F36', dark: '#0B0C0F', contrastText: '#FFFFFF' },
            secondary: { main: brand.greenDark, contrastText: '#FFFFFF' },
            success: { main: '#067647', light: '#ECFDF3', contrastText: '#FFFFFF' },
            error: { main: '#B42318', light: '#FEF3F2', contrastText: '#FFFFFF' },
            warning: { main: '#B54708', light: '#FFFAEB', contrastText: '#FFFFFF' },
            info: { main: '#175CD3', light: '#EFF8FF', contrastText: '#FFFFFF' },
            background: { default: '#F5F6F8', paper: '#FFFFFF' },
            text: { primary: '#101828', secondary: '#667085', disabled: '#98A2B3' },
            divider: '#E4E7EC',
            action: { hover: 'rgba(16, 24, 40, 0.04)' },
        },
        shape: { borderRadius: 8 },
        typography: {
            fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
            fontSize: 14,
            h1: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.02em' },
            h2: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
            h3: { fontSize: '1.25rem', fontWeight: 600 },
            h4: { fontSize: '1.125rem', fontWeight: 600 },
            h5: { fontSize: '1rem', fontWeight: 600 },
            h6: { fontSize: '0.9375rem', fontWeight: 600 },
            subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
            subtitle2: { fontSize: '0.8125rem', fontWeight: 600 },
            body1: { fontSize: '0.875rem' },
            body2: { fontSize: '0.8125rem' },
            caption: { fontSize: '0.75rem' },
            overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em' },
            button: { textTransform: 'none', fontWeight: 600 },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: { backgroundColor: '#F5F6F8' },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { borderRadius: 8, paddingInline: 14 },
                    sizeSmall: { paddingInline: 10 },
                    outlined: ({ theme: t }) => ({
                        borderColor: t.palette.divider,
                        color: t.palette.text.primary,
                        backgroundColor: '#FFFFFF',
                        '&:hover': { borderColor: '#D0D5DD', backgroundColor: '#F9FAFB' },
                    }),
                    outlinedError: ({ theme: t }) => ({
                        color: t.palette.error.main,
                        borderColor: alpha(t.palette.error.main, 0.3),
                        '&:hover': { borderColor: t.palette.error.main, backgroundColor: t.palette.error.light },
                    }),
                },
            },
            MuiIconButton: {
                styleOverrides: { root: { borderRadius: 8 } },
            },
            MuiPaper: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: { backgroundImage: 'none' },
                    outlined: ({ theme: t }) => ({ borderColor: t.palette.divider }),
                },
            },
            MuiCard: {
                defaultProps: { variant: 'outlined' },
                styleOverrides: { root: { borderRadius: 10 } },
            },
            MuiTableContainer: {
                styleOverrides: { root: { borderRadius: 10 } },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: ({ theme: t }) => ({
                        borderBottom: `1px solid ${t.palette.divider}`,
                        padding: '12px 16px',
                        fontSize: '0.8125rem',
                    }),
                    head: ({ theme: t }) => ({
                        backgroundColor: '#F9FAFB',
                        color: t.palette.text.secondary,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                    }),
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:last-child td': { borderBottom: 0 },
                        '&.MuiTableRow-hover:hover': { backgroundColor: '#F9FAFB' },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 6, fontWeight: 500 },
                    sizeSmall: { height: 22, fontSize: '0.75rem' },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: { borderRadius: 12 },
                },
            },
            MuiDialogTitle: {
                styleOverrides: { root: { fontSize: '1.0625rem', fontWeight: 600, padding: '20px 24px 8px' } },
            },
            MuiDialogContent: {
                styleOverrides: { root: { padding: '8px 24px 16px' } },
            },
            MuiDialogContentText: {
                styleOverrides: { root: { fontSize: '0.8125rem', marginBottom: 8 } },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: ({ theme: t }) => ({
                        padding: '12px 24px',
                        borderTop: `1px solid ${t.palette.divider}`,
                        gap: 8,
                    }),
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ theme: t }) => ({
                        backgroundColor: '#FFFFFF',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#98A2B3' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.palette.primary.main, borderWidth: 1 },
                        '&.Mui-disabled': { backgroundColor: '#F9FAFB' },
                    }),
                },
            },
            MuiInputLabel: {
                styleOverrides: { root: { fontSize: '0.875rem' } },
            },
            MuiBreadcrumbs: {
                styleOverrides: { root: { fontSize: '0.8125rem' } },
            },
            MuiLink: {
                styleOverrides: { root: { cursor: 'pointer' } },
            },
            MuiAccordion: {
                defaultProps: { disableGutters: true, elevation: 0 },
                styleOverrides: {
                    root: ({ theme: t }) => ({
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 8,
                        '&:before': { display: 'none' },
                        '&:not(:last-of-type)': { marginBottom: 8 },
                    }),
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: { borderRadius: 8, fontSize: '0.8125rem', alignItems: 'flex-start' },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: { backgroundColor: '#101828', fontSize: '0.75rem', fontWeight: 500 },
                },
            },
            MuiTablePagination: {
                styleOverrides: {
                    root: ({ theme: t }) => ({ borderTop: `1px solid ${t.palette.divider}` }),
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: ({ theme: t }) => ({
                        border: `1px solid ${t.palette.divider}`,
                        borderRadius: 10,
                        fontSize: '0.8125rem',
                        '--DataGrid-containerBackground': '#F9FAFB',
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '0.75rem', color: t.palette.text.secondary },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    }),
                },
            },
        },
    },
    ptBR,
);

export default theme;
